import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getJob, cancelJob, subscribeToJob } from '../../services/syncApi';
import WorkerStatusBanner from './WorkerStatusBanner';

const CANONICAL_STAGE_ORDER = ['create', 'update', 'assign_channel', 'assign_category'];

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : (n ?? '—'));

const formatDuration = (ms) => {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

// Rolling-window rate so a stage that just started doesn't show "0 it/s".
// Keeps last N samples and computes done/sec across the window.
const useRate = (currentDone, currentStage) => {
  const samplesRef = useRef([]);
  const stageRef = useRef(currentStage);
  useEffect(() => {
    if (stageRef.current !== currentStage) {
      // Stage flipped -- reset the window so the rate reflects the new stage.
      samplesRef.current = [];
      stageRef.current = currentStage;
    }
    samplesRef.current.push({ t: Date.now(), done: currentDone });
    if (samplesRef.current.length > 20) samplesRef.current.shift();
  }, [currentDone, currentStage]);
  if (samplesRef.current.length < 2) return null;
  const first = samplesRef.current[0];
  const last = samplesRef.current[samplesRef.current.length - 1];
  const elapsed = (last.t - first.t) / 1000;
  const delta = last.done - first.done;
  if (elapsed <= 0 || delta <= 0) return null;
  return delta / elapsed;
};

const StageList = ({ planned, completed, currentStage, currentStageDone, currentStageTotal }) => {
  // Use the job's declared stage_filter (canonical order) so we always show
  // the same set the worker is iterating through.
  const stages = CANONICAL_STAGE_ORDER.filter((s) => planned.includes(s));
  if (stages.length === 0) return null;
  return (
    <ol className="stage-list">
      {stages.map((s) => {
        const isDone = completed.includes(s);
        const isCurrent = !isDone && s === currentStage;
        const tag = isDone ? 'done' : isCurrent ? 'current' : 'pending';
        return (
          <li key={s} className={`stage-pill stage-${tag}`}>
            <span className="stage-marker" aria-hidden>
              {isDone ? '✓' : isCurrent ? '●' : '○'}
            </span>
            <span className="stage-name">{s}</span>
            {isCurrent && currentStageTotal > 0 && (
              <span className="stage-count">
                {fmt(currentStageDone)} / {fmt(currentStageTotal)}
              </span>
            )}
            {isDone && <span className="stage-count">done</span>}
          </li>
        );
      })}
    </ol>
  );
};

const ProgressBar = ({ done, total }) => {
  const safeTotal = Math.max(total || 0, 0);
  const safeDone = Math.min(done || 0, safeTotal);
  const pct = safeTotal > 0 ? Math.round((safeDone / safeTotal) * 100) : 0;
  return (
    <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={safeTotal} aria-valuenow={safeDone}>
      <div className="progress-bar" style={{ width: `${pct}%` }} />
      <span className="progress-text">
        {fmt(safeDone)} / {fmt(safeTotal)} ({pct}%)
      </span>
    </div>
  );
};

const Counter = ({ label, value, tone }) => (
  <div className={`counter-card counter-${tone || 'neutral'}`}>
    <div className="counter-label">{label}</div>
    <div className="counter-value">{fmt(value || 0)}</div>
  </div>
);

const ResultGrid = ({ counters, mode, dryRun }) => {
  // Surface the counter taxonomy emitted by sync_mutate / compare_market.
  // We don't show zero-valued non-error counters because the grid gets noisy
  // when only one stage is active.
  const isCompare = mode === 'compare';
  const successTone = 'success';
  const warnTone = 'warn';
  const errorTone = 'error';

  const cards = [];
  if (isCompare) {
    cards.push(
      { key: 'compared', label: 'Compared', value: counters.compared, tone: successTone, alwaysShow: true },
      { key: 'mismatched', label: 'Mismatched', value: counters.mismatched, tone: warnTone },
      { key: 'missing_in_bc', label: 'Missing in BC', value: counters.missing_in_bc, tone: warnTone },
      { key: 'match', label: 'Match', value: counters.match, tone: successTone },
    );
  } else if (dryRun) {
    cards.push(
      { key: 'would_create', label: 'Would create', value: counters.would_create, tone: warnTone },
      { key: 'would_update', label: 'Would update', value: counters.would_update, tone: warnTone, alwaysShow: true },
      { key: 'would_assign_channel', label: 'Would assign channel', value: counters.would_assign_channel, tone: warnTone },
      { key: 'would_assign_category', label: 'Would assign category', value: counters.would_assign_category, tone: warnTone },
    );
  } else {
    cards.push(
      { key: 'created', label: 'Created', value: counters.created, tone: successTone },
      // reconciled = matched an existing BC product by SKU; reused = confirmed an
      // existing committed record. Neither created a product this run, so reused
      // is warn-toned to make a no-op create visible rather than counted as Created.
      { key: 'reconciled', label: 'Reconciled', value: counters.reconciled, tone: successTone },
      { key: 'reused', label: 'Reused (no BC write)', value: counters.reused, tone: warnTone },
      { key: 'updated', label: 'Updated', value: counters.updated, tone: successTone, alwaysShow: true },
      { key: 'assigned_channel', label: 'Channel assigned', value: counters.assigned_channel, tone: successTone },
      { key: 'assigned_category', label: 'Category assigned', value: counters.assigned_category, tone: successTone },
      { key: 'writeback_done', label: 'Akeneo writeback', value: counters.writeback_done, tone: successTone },
    );
  }
  // Always surface error totals so they don't hide in the raw JSON.
  cards.push(
    { key: 'errors_total', label: 'Errors total', value: counters.errors_total, tone: errorTone, alwaysShow: true },
    { key: 'errors_validation', label: 'Validation', value: counters.errors_validation, tone: errorTone },
    { key: 'errors_not_found', label: 'Not found', value: counters.errors_not_found, tone: errorTone },
    { key: 'errors_transient', label: 'Transient', value: counters.errors_transient, tone: errorTone },
    { key: 'errors_auth', label: 'Auth', value: counters.errors_auth, tone: errorTone },
  );

  return (
    <div className="counter-grid">
      {cards
        .filter((c) => c.alwaysShow || (typeof c.value === 'number' && c.value > 0))
        .map((c) => (
          <Counter key={c.key} label={c.label} value={c.value} tone={c.tone} />
        ))}
    </div>
  );
};

const JobDetail = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [live, setLive] = useState(null);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const j = await getJob(jobId);
        if (!cancelled) setJob(j);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    const unsub = subscribeToJob(jobId, (payload) => {
      if (!cancelled) setLive(payload);
    }, () => {
      // closed
    });
    return () => { cancelled = true; unsub(); };
  }, [jobId]);

  const requestCancel = async () => {
    if (!window.confirm('Request cancel for this job?')) return;
    try {
      await cancelJob(jobId);
    } catch (e) {
      alert(`Cancel failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const merged = { ...(job || {}), ...(live || {}) };
  const counters = merged.counters || {};
  const planned = job?.stage_filter || [];
  const completed = merged.stages_completed || [];
  const currentStage = counters.current_stage || null;
  const currentStageDone = counters.current_stage_done || 0;
  const currentStageTotal = counters.current_stage_total || 0;
  const isRunning = ['pending', 'running'].includes(merged.status);

  // Compare-mode uses `compared`/`total`; mutate uses per-stage counters.
  const isCompare = job?.mode === 'compare';
  const currentStageDoneSafe = Math.min(currentStageDone, currentStageTotal || currentStageDone);
  const stageDoneFraction =
    currentStageTotal > 0 ? currentStageDoneSafe / currentStageTotal : 0;

  // Equal-weight overall progress across the job's planned stages. Stage
  // sizes vary (update is N+1 per SKU, assignments are chunked), but for a
  // "am I 25% or 75% done?" indicator equal-weight is closer to perception
  // than items-weighted -- which would make update dominate visually.
  const stagesPlanned = planned.length;
  let overallFraction = 0;
  if (isCompare) {
    overallFraction =
      counters.total > 0 ? Math.min(1, (counters.compared || 0) / counters.total) : 0;
  } else if (stagesPlanned > 0) {
    const completedCount = completed.filter((s) => planned.includes(s)).length;
    overallFraction = (completedCount + stageDoneFraction) / stagesPlanned;
  }
  const overallPct = Math.round(overallFraction * 100);

  // The rate measurement still runs over the *current* progress signal --
  // for compare that's `compared`, for mutate it's the current stage.
  const rateDone = isCompare ? counters.compared || 0 : currentStageDoneSafe;
  const rate = useRate(rateDone, currentStage || (isCompare ? 'compare' : null));
  const startedAt = job?.started_at ? new Date(job.started_at).getTime() : null;
  const elapsedMs = startedAt ? Date.now() - startedAt : null;

  // ETA for the *current* stage (the only one we have a total for), since
  // we don't know subsequent stages' sizes until they start.
  const currentStageRemaining =
    currentStageTotal > 0 && currentStageDoneSafe < currentStageTotal
      ? currentStageTotal - currentStageDoneSafe
      : 0;
  const compareRemaining =
    counters.total > 0 ? Math.max(0, counters.total - (counters.compared || 0)) : 0;
  const remaining = isCompare ? compareRemaining : currentStageRemaining;
  const etaSec = rate && rate > 0 && remaining > 0 ? remaining / rate : null;

  // Position label for the stage strip: "Stage 2 of 4: assign_channel".
  const completedInPlanned = completed.filter((s) => planned.includes(s)).length;
  const currentStageIndex =
    currentStage && planned.includes(currentStage)
      ? planned.indexOf(currentStage) + 1
      : completedInPlanned + 1;

  if (error) return <div className="error">Error: {error}</div>;
  if (!job) return <div>Loading...</div>;

  return (
    <div className="job-detail">
      <header>
        <h1>Job #{job.id}</h1>
        {isRunning && (
          <button type="button" onClick={requestCancel}>Cancel</button>
        )}
      </header>

      {isRunning && <WorkerStatusBanner marketCode={job.store_code} />}

      <section className="card">
        <p className="job-meta">
          <strong>Store</strong>: {job.store_code} &nbsp;
          <strong>Mode</strong>: {job.mode} &nbsp;
          <strong>Stages</strong>: {planned.join(', ') || '(all)'} &nbsp;
          <strong>Dry run</strong>: {job.dry_run ? 'yes' : 'no'} &nbsp;
          <strong>Created by</strong>: {job.created_by}
        </p>
        <p className="job-meta">
          <strong>Status</strong>: <span className={`status status-${merged.status}`}>{merged.status}</span>
          {merged.last_heartbeat_at && (
            <> &nbsp; <strong>Heartbeat</strong>: {new Date(merged.last_heartbeat_at).toLocaleTimeString()}</>
          )}
          {startedAt && <> &nbsp; <strong>Elapsed</strong>: {formatDuration(elapsedMs)}</>}
        </p>

        {(job.mode === 'mutate' || isCompare) && (
          <>
            <div className="overall-progress">
              <div className="overall-progress-header">
                <span className="overall-progress-label">
                  {isCompare
                    ? 'Overall'
                    : stagesPlanned > 1
                      ? `Stage ${currentStageIndex} of ${stagesPlanned}${currentStage ? `: ${currentStage}` : ''}`
                      : `Stage: ${currentStage || planned[0] || ''}`}
                </span>
                <span className="overall-progress-pct">{overallPct}%</span>
              </div>
              <div className="progress progress-lg" role="progressbar"
                aria-valuemin={0} aria-valuemax={100} aria-valuenow={overallPct}>
                <div className="progress-bar" style={{ width: `${overallPct}%` }} />
              </div>
              <p className="progress-meta">
                {rate ? `${rate.toFixed(1)} items/sec` : 'measuring…'}
                {etaSec ? <> &nbsp;·&nbsp; ETA {formatDuration(etaSec * 1000)} (current stage)</> : null}
                {!isCompare && stagesPlanned > 1 && (
                  <> &nbsp;·&nbsp; <span className="muted">overall ETA depends on remaining stages' sizes</span></>
                )}
              </p>
            </div>

            {job.mode === 'mutate' && (
              <>
                <h3 className="section-h">Stages</h3>
                <StageList
                  planned={planned}
                  completed={completed}
                  currentStage={currentStage}
                  currentStageDone={currentStageDoneSafe}
                  currentStageTotal={currentStageTotal}
                />
              </>
            )}

            {!isCompare && currentStageTotal > 0 && (
              <>
                <h3 className="section-h">Current stage: {currentStage}</h3>
                <ProgressBar done={currentStageDoneSafe} total={currentStageTotal} />
              </>
            )}
          </>
        )}

        <h3 className="section-h">Outcomes</h3>
        {job.mode === 'mutate' && !isCompare && counters.no_desired_rows && (
          <div className="banner warning" role="alert">
            Nothing matched: 0 publishable rows for this store. The SKU(s) are likely
            unpublished, incomplete, or not present, so no products were created or updated.
          </div>
        )}
        <ResultGrid counters={counters} mode={job.mode} dryRun={job.dry_run} />

        {merged.error_class && (
          <div className="error job-error">
            <strong>Error ({merged.error_class}):</strong> {merged.error_detail}
          </div>
        )}

        <div className="raw-toggle">
          <button type="button" className="link" onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? 'Hide' : 'Show'} raw counters JSON
          </button>
        </div>
        {showRaw && <pre className="raw-counters">{JSON.stringify(counters, null, 2)}</pre>}
      </section>
    </div>
  );
};

export default JobDetail;
